import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Truck, Shield, TrendingUp } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';

const REDIRECT_URL = window.location.origin + '/';

export default function Auth() {
  const [masterCode, setMasterCode] = useState('');
  const [userName, setUserName] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Check for error in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    if (error === 'pin_not_assigned') {
      toast.error('PIN not found! Contact master admin to get your PIN assigned.');
    } else if (error === 'invalid_pin') {
      toast.error('Invalid PIN format. Must be 4 digits.');
    } else if (error === 'suspended') {
      toast.error('This franchise has been suspended due to non-payment. Contact Urbanvision Logistics to resolve.');
    }
  }, []);

  const handleDirectLogin = () => {
    setLoading(true);
    // Force full page reload to login endpoint
    window.location.href = `${window.location.origin}/api/auth/test-session-redirect`;
  };

  const handleGoogleLogin = () => {
    const isSuperAdmin = masterCode === '3357';
    const isOrgBroker = masterCode && masterCode.length === 6 && /^\d+$/.test(masterCode);
    const isAgent = !masterCode;
    
    // Agents must have a PIN code
    if (isAgent && !pinCode) {
      toast.error('Agents must create a 4-digit PIN code');
      return;
    }
    
    if (isAgent && (pinCode.length !== 4 || !/^\d+$/.test(pinCode))) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    
    if (masterCode) {
      localStorage.setItem('master_code', masterCode);
    }
    if (userName) {
      localStorage.setItem('user_name', userName);
    }
    if (pinCode) {
      localStorage.setItem('pin_code', pinCode);
    }
    
    window.location.href = `${window.location.origin}/api/auth/direct-login?code=${masterCode}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left Side - Branding */}
          <div className="text-white space-y-6 animate-fade-in">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-3 rounded-xl">
                <Truck className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight">Urbanvision Logistics</h1>
                <p className="text-slate-300 text-sm mt-1">LLC</p>
              </div>
            </div>
            
            <p className="text-xl text-slate-300 leading-relaxed">
              Complete freight broker management platform with real-time load tracking, carrier management, and automated documentation.
            </p>

            <div className="space-y-4 pt-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold">Role-Based Access Control</h3>
                  <p className="text-slate-400 text-sm">Secure broker and agent access with role-based permissions</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <TrendingUp className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold">Real-Time Analytics</h3>
                  <p className="text-slate-400 text-sm">Track revenue, profit margins, and load status in real-time</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <Card className="shadow-2xl border-slate-700 bg-white/95 backdrop-blur animate-fade-in" data-testid="auth-card">
            <CardHeader>
              <CardTitle className="text-2xl">Sign In</CardTitle>
              <CardDescription>
                Brokers: Enter master code | Agents: Enter your assigned PIN
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="masterCode">Master Code (Optional)</Label>
                <Input
                  id="masterCode"
                  data-testid="master-code-input"
                  type="password"
                  placeholder="Enter your master code"
                  value={masterCode}
                  onChange={(e) => setMasterCode(e.target.value)}
                  className="h-12"
                />
                <p className="text-xs text-slate-500">
                  Brokers: Enter your master code | Agents: leave blank
                </p>
              </div>

              {!masterCode && (
                <div className="space-y-2">
                  <Label htmlFor="pinCode">Agent PIN (Required if no master code)</Label>
                  <Input
                    id="pinCode"
                    data-testid="pin-code-input"
                    type="password"
                    placeholder="Enter your assigned 4-digit PIN"
                    value={pinCode}
                    onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    maxLength={4}
                    className="h-12"
                  />
                  <p className="text-xs text-red-500 font-medium">
                    ⚠️ PIN must be assigned by master admin first
                  </p>
                </div>
              )}

              <Button
                onClick={() => {
                  setLoading(true);
                  const params = new URLSearchParams();
                  if (masterCode) params.append('code', masterCode);
                  if (pinCode) params.append('pin', pinCode);
                  window.location.href = `${window.location.origin}/api/auth/direct-login?${params.toString()}`;
                }}
                disabled={loading}
                data-testid="login-btn"
                className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Logging in...' : 'LOGIN'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
